/**
 * @author    Nikko Renolds
 * @package   Trap Tycoon
 * @copyright (c) Fallox Developments 2026.
 * @license   MIT
 * @description Detailed management view for Girl units.
 */
import React, { useState } from "react";
import {
  Star,
  Heart,
  TrendingUp,
  Users,
  Filter,
  ArrowUpDown,
} from "lucide-react";

const RARITY_COLORS = {
  common: "text-zinc-400 border-zinc-700",
  rare: "text-blue-400 border-blue-700",
  epic: "text-purple-400 border-purple-700",
  legendary: "text-yellow-400 border-yellow-700",
};

export default function GirlsManagement({ inventory, happiness, onBack }) {
  const [rarityFilter, setRarityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("income_desc");

  const girls = inventory.filter((i) => i.type === "girl");
  const totalIncome = girls.reduce(
    (acc, girl) => acc + (girl.passive_income || 0) * girl.quantity,
    0,
  );

  const filteredGirls = girls
    .filter((g) => rarityFilter === "all" || g.rarity === rarityFilter)
    .sort((a, b) => {
      if (sortBy === "income_desc") return b.passive_income - a.passive_income;
      if (sortBy === "income_asc") return a.passive_income - b.passive_income;
      if (sortBy === "qty") return b.quantity - a.quantity;
      return 0;
    });

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-right duration-300">
      <div className="flex items-center justify-between border-b border-pink-900 pb-4">
        <div>
          <h2 className="text-3xl font-black italic text-pink-500 uppercase flex items-center gap-2">
            <Star size={28} /> Pimp Management
          </h2>
          <p className="text-xs text-pink-400 font-mono uppercase tracking-widest">
            Pimp or Be Pimped
          </p>
        </div>
        <button
          onClick={onBack}
          className="px-6 py-2 bg-zinc-900 border border-zinc-700 text-zinc-300 hover:text-white hover:border-white transition-all uppercase font-bold text-xs"
        >
          &larr; Back to Trap
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-black border border-pink-900/50 p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[10px] text-pink-700 uppercase font-bold flex items-center gap-1">
                <Heart size={12} /> Overall Happiness
              </p>
              <p className="text-2xl font-black text-white">{happiness}%</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-zinc-500 uppercase">Impact</p>
              <p className="text-xs text-green-400 font-bold">
                {happiness >= 90
                  ? "+20% Income"
                  : happiness >= 50
                    ? "Normal"
                    : "-20% Income"}
              </p>
            </div>
          </div>
          <div className="w-full h-1 bg-zinc-900 mt-3">
            <div
              className={`h-full transition-all duration-500 ${
                happiness > 70
                  ? "bg-green-500"
                  : happiness > 30
                    ? "bg-yellow-500"
                    : "bg-red-500"
              }`}
              style={{ width: `${happiness}%` }}
            />
          </div>
        </div>

        <div className="bg-black border border-green-900/50 p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[10px] text-green-700 uppercase font-bold flex items-center gap-1">
                <TrendingUp size={12} /> Total Passive Income
              </p>
              <p className="text-2xl font-black text-green-400">
                ${totalIncome.toLocaleString()}/tick
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 bg-zinc-950 p-3 border border-zinc-800 items-center">
        <div className="flex items-center gap-2 bg-black border border-zinc-700 px-3 py-1">
          <Filter size={14} className="text-zinc-500" />
          <select
            value={rarityFilter}
            onChange={(e) => setRarityFilter(e.target.value)}
            className="bg-transparent text-[10px] text-white outline-none uppercase cursor-pointer"
          >
            <option value="all" className="bg-black text-white">
              All Tiers
            </option>
            <option value="common" className="bg-black text-white">
              Common
            </option>
            <option value="rare" className="bg-black text-white">
              Rare
            </option>
            <option value="epic" className="bg-black text-white">
              Epic
            </option>
            <option value="legendary" className="bg-black text-white">
              Legendary
            </option>
          </select>
        </div>

        <div className="flex items-center gap-2 bg-black border border-zinc-700 px-3 py-1">
          <ArrowUpDown size={14} className="text-zinc-500" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-transparent text-[10px] text-white outline-none uppercase cursor-pointer"
          >
            <option value="income_desc" className="bg-black text-white">
              Highest Income
            </option>
            <option value="income_asc" className="bg-black text-white">
              Lowest Income
            </option>
            <option value="qty" className="bg-black text-white">
              Quantity
            </option>
          </select>
        </div>
      </div>

      {/* Unit List */}
      <div className="grid grid-cols-1 gap-3">
        {filteredGirls.length === 0 ? (
          <div className="p-8 text-center border-2 border-dashed border-zinc-800 text-zinc-600 font-mono uppercase text-xs">
            No talent found matching these filters.
          </div>
        ) : (
          filteredGirls.map((unit) => (
            <div
              key={unit.id}
              className={`bg-zinc-950 border-l-4 p-4 flex justify-between items-center ${
                RARITY_COLORS[unit.rarity] || RARITY_COLORS.common
              } border-y border-r border-zinc-900`}
            >
              <div className="flex items-center gap-4">
                <div className="bg-black p-3 border border-zinc-800">
                  <Users
                    size={20}
                    className={RARITY_COLORS[unit.rarity]?.split(" ")[0]}
                  />
                </div>
                <div>
                  <h4 className="font-black text-white uppercase italic text-sm">
                    {unit.name}
                  </h4>
                  <p className="text-[10px] text-zinc-500 font-mono uppercase">
                    {unit.rarity} | Qty:{" "}
                    <span className="text-white">{unit.quantity}</span>
                  </p>
                </div>
              </div>

              <div className="text-right">
                <p className="text-[10px] text-zinc-500 uppercase font-bold">
                  Earnings
                </p>
                <p className="text-sm font-bold text-green-400">
                  ${(unit.passive_income * unit.quantity).toLocaleString()}/tick
                </p>
                <p className="text-[9px] text-zinc-600">
                  (${unit.passive_income} ea)
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
